import type { PhaseBalanceResult } from '../../types/engineering.ts'
import { AsOfStamp } from './AsOfStamp.tsx'

interface PhaseBalanceChartProps {
  data: PhaseBalanceResult | undefined
  isLoading: boolean
}

const PHASE_COLORS: Record<string, string> = {
  A: '#e74c3c',
  B: '#f1c40f',
  C: '#3498db',
}

export function PhaseBalanceChart({ data, isLoading }: PhaseBalanceChartProps) {
  if (isLoading) {
    return <div className="text-sm text-gray-400 p-2">Loading phase data...</div>
  }

  if (!data || data.phases.length === 0) {
    return <div className="text-sm text-gray-400 p-2">No phase data available</div>
  }

  const maxKva = Math.max(...data.phases.map(p => p.total_kva), 1)

  // E2: is_balanced is null when the GIS phase of too many transformers is a
  // numeric count (or NULL) — the imbalance figure is then an artifact of the
  // encoding, so neither the verdict nor the percentage is presented.
  const identityUnknown = data.is_balanced === null
  const chipClass = identityUnknown
    ? 'bg-gray-100 text-gray-600'
    : data.is_balanced
      ? 'bg-green-100 text-green-700'
      : 'bg-orange-100 text-orange-700'
  const chipLabel = identityUnknown
    ? 'Phase identity unknown'
    : data.is_balanced
      ? 'Balanced'
      : 'Imbalanced'

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Phase Balance</h3>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${chipClass}`}>
            {chipLabel}
          </span>
          <span className="text-xs text-gray-500">
            {identityUnknown
              ? `${data.phase_identity_unknown_count} transformers with no phase identity`
              : `${data.imbalance_pct}% imbalance`}
          </span>
        </div>
      </div>
      <AsOfStamp computedAt={null} />

      {/* Horizontal bar chart */}
      <div className="space-y-2">
        {data.phases.map(phase => (
          <div key={phase.phase} className="flex items-center gap-3">
            <div className="w-8 text-right">
              <span
                className="text-sm font-bold"
                style={{ color: PHASE_COLORS[phase.phase] || '#666' }}
              >
                {phase.phase}
              </span>
            </div>
            <div className="flex-1">
              <div className="h-6 bg-gray-100 rounded overflow-hidden relative">
                <div
                  className="h-full rounded transition-all duration-300"
                  style={{
                    width: `${(phase.total_kva / maxKva) * 100}%`,
                    backgroundColor: PHASE_COLORS[phase.phase] || '#666',
                    opacity: 0.7,
                  }}
                />
                <div className="absolute inset-0 flex items-center px-2">
                  <span className="text-xs font-medium text-gray-800">
                    {phase.total_kva.toLocaleString()} kVA ({phase.pct_of_total}%)
                  </span>
                </div>
              </div>
            </div>
            <div className="w-24 text-right text-xs text-gray-500">
              {phase.transformer_count} xfmrs
              <br />
              {phase.meter_count} meters served
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
